<template>
  <section class="chart-statistics-container">
    <el-container class="chart-statistics package-title">
      <el-main>
        <h1>{{ $t('pndPackages.tile.title') }}</h1>
      </el-main>
    </el-container>

    <div
      class="chart-statistics"
      style="padding-top: 10px;"
    >
      <TileRowDivider
        :space-for-logo="false"
        :column-label01="$t('pndPackages.tile.packageType.hgv')"
        :column-label02="$t('pndPackages.tile.packageType.libert')"
        :column-label03="$t('pndPackages.tile.packageType.obu')"
      />

      <div class="chart-statistic-row">
        <div
          v-loading="queryLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions()"
            :series="pndPackagesDonutSeries.hgv"
          />
        </div>

        <div
          v-loading="queryLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions()"
            :series="pndPackagesDonutSeries.libert"
          />
        </div>

        <div
          v-loading="queryLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions()"
            :series="pndPackagesDonutSeries.obu"
          />
        </div>
      </div>

      <TileRowDivider :space-for-logo="false" />

      <!------------------------- INFO START ------------------------->

      <div class="bottom-info-container">
        <div class="bottom-info">
          <el-row>
            <el-col
              :span="24"
              class="colors-info"
            >
              <el-tag>
                <span class="coloured-dot red" />
                <span>{{ $t('pndPackages.tile.shortStatistics.older') }}</span>
              </el-tag>

              <el-tag>
                <span class="coloured-dot yellow" />
                <span>{{ $t('pndPackages.tile.shortStatistics.recent') }}</span>
              </el-tag>

              <el-tag>
                <span class="coloured-dot green" />
                <span>{{ $t('pndPackages.tile.shortStatistics.today') }}</span>
              </el-tag>
            </el-col>
          </el-row>
        </div>
      </div>
    </div>

    <!------------------------- INFO END ------------------------->
  </section>
</template>

<script lang="ts">
import { Vue } from 'vue-property-decorator'
import Component from 'vue-class-component'

import {
  Maybe,
  PndTileStatisticsQuery,
  PndTileStatistics
} from '@/types/graphql'
import { ApolloQueryResult } from 'apollo-client'
import TileRowDivider from '@/components/UI/TileRowDivider.vue'

@Component({
  components: {
    TileRowDivider
  }
})

export default class PNDPackagesTile extends Vue {
  // State

  private queryLoading = false

  private queryResult: Maybe<PndTileStatistics> = null

  // Computed getters

  private getSeriesWithEmptyState(older: number, recent: number, today: number): number[] {
    const hasData = older + recent + today > 0
    // When no data, return [0, 0, 0, 1] - the 4th value will be gray
    return hasData ? [older, recent, today] : [0, 0, 0, 1]
  }

  private get pndPackagesDonutSeries() {
    if (!this.queryResult) {
      return {
        hgv: [0, 0, 0, 1],
        libert: [0, 0, 0, 1],
        obu: [0, 0, 0, 1]
      }
    }

    return {
      hgv: this.getSeriesWithEmptyState(
        this.queryResult.hgv.older,
        this.queryResult.hgv.recent,
        this.queryResult.hgv.today
      ),
      libert: this.getSeriesWithEmptyState(
        this.queryResult.libert.older,
        this.queryResult.libert.recent,
        this.queryResult.libert.today
      ),
      obu: this.getSeriesWithEmptyState(
        this.queryResult.obu.older,
        this.queryResult.obu.recent,
        this.queryResult.obu.today
      )
    }
  }

  private getDonutOptions() {
    return {
      chart: {
        width: '60%',
        type: 'donut',
        animations: {
          enabled: false
        }
      },
      legend: { show: false },
      dataLabels: { enabled: false },
      colors: ['hsl(0, 100%, 50%)', 'hsl(47, 100%, 50%)', 'hsl(161, 67%, 47%)', 'hsl(222, 32%, 92%)'],
      plotOptions: {
        pie: {
          customScale: 1,
          expandOnClick: false,
          donut: {
            size: '87%',
            labels: {
              show: true,
              name: {
                show: false
              },
              value: {
                show: true,
                fontSize: '16px',
                offsetY: -12
              },
              total: {
                show: true,
                showAlways: true,
                label: '',
                formatter: function(w: any) {
                  return w.globals.seriesTotals.slice(0, 3)
                }
              }
            }
          }
        }
      },
      stroke: { show: true, width: 0 },
      tooltip: {
        enabled: false
      },
      states: {
        normal: {
          filter: {
            type: 'none',
            value: 0
          }
        },
        hover: {
          filter: {
            type: 'none',
            value: 0
          }
        },
        active: {
          filter: {
            type: 'none',
            value: 0
          }
        }
      }
    }
  }

  // Methods

  private async fetchPNDPackagesTileStatistics() {
    this.queryLoading = true

    try {
      const result: ApolloQueryResult<PndTileStatisticsQuery> = await this.$apollo.query({
        query: PndTileStatisticsQuery,
        fetchPolicy: 'network-only'
      })

      this.queryResult = result.data.pndTileStatistics
    } catch (error) {
      console.error('[fetchPNDPackagesTileStatistics] ERROR => ' + error)
    }

    this.queryLoading = false
  }

  // Hooks

  private mounted() {
    this.fetchPNDPackagesTileStatistics()
  }
}
</script>

<style lang="scss">
  @import "@/css/chartStatisticsTile.scss";
</style>
